import { useEffect, useRef } from 'react';
import { primaryLeg, resultState, type ResultState } from '@delaybuddy/contract';
import { useNextStep } from '../../app/navigation';
import { Screen } from '../../chrome';
import {
  AskTitle,
  Board,
  Button,
  CarrierLogo,
  Flap,
  Hint,
  Note,
  RouteStrip,
  Spinner,
  TextLink,
} from '../../components';
import { useT } from '../../i18n';
import { useTrack } from '../../lib/analytics';
import { useSetResultResponse, type Enquiry, type ResultResponse } from '../../lib/api';
import { formatDate, formatMoney } from '../../lib/format';
import { StageError, TripSummary } from '../S1/stage';
import { useStage } from '../S1/trip';
import { FLAP_BY_STATUS, hhmm, humanMins } from '../S3/flight';
import './S5.css';

const REGIME_LABEL = { EC261: 'S5.regime.EC261', UK261: 'S5.regime.UK261', none: 'S5.regime.none' } as const;

/**
 * S5 · Here's what we know — the payoff. Three states, one board (ux-flow.md S5), plus the
 * contract's `not_covered` (regime none: shown like below-threshold, no money, and the check
 * carries on to S8 — design rule 4; DECISION-NEEDED copy). The figure is the quote's "up to".
 */
export default function ScreenS5() {
  const t = useT();
  const track = useTrack();
  const { enquiryId, enquiry, trip, isLoading, error } = useStage('S5');
  const respond = useSetResultResponse(enquiryId);
  const { goNext } = useNextStep();
  const state = enquiry ? resultState(enquiry) : null;

  const shown = useRef(false);
  useEffect(() => {
    if (!state || shown.current) return;
    shown.current = true;
    track('s5_shown', { state });
  }, [state, track]);

  async function answer(response: ResultResponse) {
    track('s5_answered', { state, response });
    const e = await respond.mutateAsync({ response });
    await goNext(e);
  }
  const onAnswer = (r: ResultResponse) => void answer(r).catch(() => {});

  const busy = respond.isPending;
  const dock = !state ? undefined : state === 'pending' ? (
    <Button onClick={() => onAnswer('confirmed')} disabled={busy}>
      {t('S5.pending.yes')}
    </Button>
  ) : (
    <>
      <Button onClick={() => onAnswer('confirmed')} disabled={busy}>
        {t(state === 'below_threshold' ? 'S5.below.yes' : 'S5.eligible.yes')}
      </Button>
      <TextLink onClick={() => onAnswer('disputed')} disabled={busy}>
        {t(state === 'below_threshold' ? 'S5.below.no' : 'S5.eligible.no')}
      </TextLink>
    </>
  );

  return (
    <Screen id="S5" title={t('S5.title')} dock={dock}>
      {error ? (
        <StageError />
      ) : isLoading || !enquiry || !state ? (
        <Spinner />
      ) : (
        <>
          <ResultBoard enquiry={enquiry} state={state} />
          <Ask enquiry={enquiry} state={state} />
          {respond.isError && <Hint warn>{t('S5.saveFailed')}</Hint>}
          <TripSummary enquiry={enquiry} trip={trip} editTo="S3" />
        </>
      )}
    </Screen>
  );
}

function ResultBoard({ enquiry, state }: { enquiry: Enquiry; state: ResultState }) {
  const t = useT();
  const leg = primaryLeg(enquiry)!;
  const f = leg.flight;
  const { origin, destination } = enquiry.trip;
  const flap = FLAP_BY_STATUS[f?.displayStatus ?? 'pending'];
  const money = enquiry.quote?.amountPerPassenger;
  const withMoney = !!money && (state === 'eligible' || state === 'pending');
  const distance = enquiry.quote?.distanceKm ?? f?.distanceKm ?? null;
  const regime = enquiry.quote?.regime;

  let actual: string;
  if (!f || f.displayStatus === 'pending') actual = t('S5.actual.noData');
  else if (f.status === 'cancelled') actual = t('S5.actual.never');
  else if (f.status === 'diverted') actual = t('S5.actual.diverted');
  else if (f.arrivalDelayMin && f.arrivalDelayMin > 0)
    actual = t('S5.actual.late', { time: hhmm(f.actualInLocal), delay: humanMins(t, f.arrivalDelayMin) });
  else actual = t('S5.actual.onTime', { time: hhmm(f.actualInLocal) });

  return (
    <Board>
      {origin && destination && <RouteStrip from={origin} to={destination} />}
      <div className="board__flight">
        <span className="board__ident">
          {f?.operatingIdent ?? leg.manualIdent}
          <span className="board__date">
            {f && <CarrierLogo src={f.operator.logoUrl} />}
            {f ? `${f.operator.name} · ${formatDate(leg.date)}` : formatDate(leg.date)}
          </span>
        </span>
        <Flap text={t(flap.key)} tone={flap.tone} />
      </div>
      {withMoney && (
        <div className="money">
          <div className="money__lede">{t('S5.money.lede')}</div>
          <Flap text={formatMoney(money)} tone="gold" animate />
          <div className="money__per">
            {t(state === 'pending' ? 'S5.money.perIfConfirmed' : 'S5.money.per')}
          </div>
          {/* Q4 (w5-06): over 3,500 km inside the regime's area is paid the middle band; say
              why, in the regime's own figures (w6-02). */}
          {enquiry.quote?.intraCommunityCap && (regime === 'EC261' || regime === 'UK261') && (
            <p className="money__cap">
              {t(regime === 'UK261' ? 'S5.money.cap.UK261' : 'S5.money.cap', { amount: formatMoney(money) })}
            </p>
          )}
        </div>
      )}
      <ul className="facts">
        <li>
          <span>{t('S5.facts.scheduled')}</span>
          <b>{hhmm(f?.scheduledInLocal)}</b>
        </li>
        <li>
          <span>{t('S5.facts.actual')}</span>
          <b>{actual}</b>
        </li>
        {distance !== null && (
          <li>
            <span>{t('S5.facts.distance')}</span>
            <b>{t('S5.facts.km', { km: distance.toLocaleString('en-GB') })}</b>
          </li>
        )}
        {regime && (
          <li>
            <span>{t('S5.facts.rule')}</span>
            <b>{t(REGIME_LABEL[regime])}</b>
          </li>
        )}
      </ul>
    </Board>
  );
}

function Ask({ enquiry, state }: { enquiry: Enquiry; state: ResultState }) {
  const t = useT();
  if (state === 'eligible')
    return (
      <>
        <AskTitle>{t('S5.eligible.ask')}</AskTitle>
        <Note>{t('S5.eligible.note')}</Note>
      </>
    );
  if (state === 'pending')
    return (
      <>
        <AskTitle>{t('S5.pending.ask')}</AskTitle>
        <Note>{t('S5.pending.note')}</Note>
      </>
    );
  if (state === 'not_covered')
    return (
      <>
        <AskTitle>{t('S5.eligible.ask')}</AskTitle>
        <Note>{t('S5.notCovered.note')}</Note>
      </>
    );
  const delay = primaryLeg(enquiry)?.flight?.arrivalDelayMin ?? 0;
  const what = delay > 0 ? t('S5.below.late', { delay: humanMins(t, delay) }) : t('S5.below.onTime');
  return (
    <>
      <AskTitle>{t('S5.below.ask', { what })}</AskTitle>
      <Note>{t('S5.below.note')}</Note>
    </>
  );
}
